import React from "react"
import { Helmet } from "react-helmet-async"
import { Truck, Clock, MapPin, Phone, CheckCircle } from "lucide-react"

const Shipping: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Shipping Information - Anmol Furniture Bazar</title>
        <meta 
          name="description" 
          content="Learn about our shipping and delivery services. Free delivery on orders above ₹25,000. Fast and safe furniture delivery across Maharashtra." 
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Shipping & Delivery Information</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fast, safe, and reliable delivery of your furniture right to your doorstep
            </p>
          </div>

          {/* Free Delivery Banner */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl mb-12 text-center">
            <div className="flex items-center justify-center mb-2">
              <Truck className="w-8 h-8 mr-2" />
              <h2 className="text-2xl font-bold">FREE DELIVERY</h2>
            </div>
            <p className="text-lg">On orders above ₹25,000 across our delivery zones</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Delivery Information */}
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <MapPin className="w-6 h-6 mr-2 text-primary-600" />
                  Delivery Areas
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Primary Delivery Zone (FREE on orders ₹25,000+):</h3>
                    <ul className="text-gray-700 space-y-1 ml-4">
                      <li>• Virar East & West</li>
                      <li>• Thane District</li>
                      <li>• Mumbai Suburbs</li>
                      <li>• Palghar District</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Extended Delivery Zone (Charges Apply):</h3>
                    <ul className="text-gray-700 space-y-1 ml-4">
                      <li>• Other parts of Maharashtra</li>
                      <li>• Contact for specific locations</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <Clock className="w-6 h-6 mr-2 text-primary-600" />
                  Delivery Timeline
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Ready-to-Ship Items</span>
                    <span className="font-semibold text-primary-600">2-5 Business Days</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Custom/Made-to-Order</span>
                    <span className="font-semibold text-primary-600">15-30 Business Days</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-700">Bulk Orders</span>
                    <span className="font-semibold text-primary-600">Contact for Timeline</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-700">Express Delivery</span>
                    <span className="font-semibold text-primary-600">Available on Request</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Process & Charges */}
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Delivery Process</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Order Confirmation</h3>
                      <p className="text-gray-600 text-sm">We confirm your order and provide estimated delivery date</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Dispatch Notification</h3>
                      <p className="text-gray-600 text-sm">SMS/Call notification when your order is dispatched</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Delivery Scheduling</h3>
                      <p className="text-gray-600 text-sm">Our team calls to schedule convenient delivery time</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-bold text-sm">4</div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Safe Delivery</h3>
                      <p className="text-gray-600 text-sm">Careful delivery and optional assembly service</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Delivery Charges</h2>
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span className="font-semibold">FREE DELIVERY</span>
                    </div>
                    <p className="text-green-600 text-sm">Orders above ₹25,000 in primary delivery zone</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Orders below ₹25,000</span>
                      <span className="font-semibold">₹500 - ₹2,000</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-700">Extended zones</span>
                      <span className="font-semibold">₹1,000 - ₹5,000</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700">Assembly service</span>
                      <span className="font-semibold">₹300 - ₹1,500</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="mt-12 bg-blue-50 p-6 rounded-xl border border-blue-200">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">Important Delivery Notes</h2>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Delivery charges calculated based on distance, item size, and weight</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Installation/assembly service available at additional cost</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Delivery team will call 30-60 minutes before arrival</span>
              </li>
              <li className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>Please ensure adequate access and space for delivery</span>
              </li>
            </ul>
          </div>

          {/* Contact Section */}
          <div className="mt-12 text-center bg-white p-8 rounded-xl shadow-lg">
            <Phone className="w-12 h-12 text-primary-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Need Help with Delivery?</h2>
            <p className="text-gray-600 mb-4">Contact our delivery team for any questions or special requirements</p>
            <div className="space-y-2">
              <p className="text-lg"><span className="font-semibold">Call:</span> +91 8010271912</p>
              <p className="text-lg"><span className="font-semibold">WhatsApp:</span> +91 87654 32109</p>
              <p className="text-lg"><span className="font-semibold">Email:</span> support@anmolfurniture.com</p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Shipping